import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Loader2, Users } from "lucide-react";
import { format } from "date-fns";

interface CriteriaRecord {
  id: number;
  name: string;
  description?: string;
  criteriaType: string;
  matchingUserCount?: number;
}

interface ConversionEntry {
  id: number;
  email: string;
  phone?: string | null;
  userId?: number | null;
  createdAt: string;
  source?: string | null;
}

interface ConversionStatsResponse {
  criteriaId: number;
  criteriaName: string;
  pendingCount: number;
  convertedCount: number;
  totalTracked: number;
  conversionRate: number;
  conversions: ConversionEntry[];
}

interface DropoutEntry {
  id: string;
  email: string;
  phone?: string | null;
  userId: number;
  unsubscribedAt: string;
  reason?: string | null;
  ipAddress?: string | null;
}

interface DropoutStatsResponse {
  criteriaId: number;
  criteriaName: string;
  totalDropouts: number;
  dropouts: DropoutEntry[];
}

interface BulkContactEntry {
  id: number;
  email: string;
  firstName: string;
  lastName: string;
  phone?: string | null;
  createdAt: string;
  status: "pending";
}

interface BulkContactsResponse {
  criteriaId: number;
  criteriaName: string;
  totalPending: number;
  contacts: BulkContactEntry[];
  limit: number;
  offset: number;
}

export default function CRMConversionsTab() {
  const [selectedCriteriaId, setSelectedCriteriaId] = useState<number | null>(null);
  const [contactsOffset, setContactsOffset] = useState(0);

  const {
    data: criteriaData,
    isLoading: isCriteriaLoading,
    error: criteriaError,
  } = useQuery<{ criteria: CriteriaRecord[] }>({
    queryKey: ["/api/admin/crm/criteria", "bulk-import"],
    queryFn: async () => {
      const response = await apiRequest("GET", "/api/admin/crm/criteria?limit=200");
      return response.json();
    },
  });

  const bulkCriteria = useMemo(
    () =>
      (criteriaData?.criteria || []).filter(
        (item) => item.criteriaType === "bulk_import"
      ),
    [criteriaData]
  );

  useEffect(() => {
    if (!selectedCriteriaId && bulkCriteria.length) {
      setSelectedCriteriaId(bulkCriteria[0].id);
    }
  }, [bulkCriteria, selectedCriteriaId]);

  // Reset contacts pagination when criteria changes
  useEffect(() => {
    setContactsOffset(0);
  }, [selectedCriteriaId]);

  const { data: conversionData, isLoading: isConversionLoading, error: conversionError } =
    useQuery<ConversionStatsResponse>({
      queryKey: ["bulk-import-conversions", selectedCriteriaId],
      enabled: Boolean(selectedCriteriaId),
      queryFn: async () => {
        if (!selectedCriteriaId) {
          throw new Error("No criteria selected");
        }
        const response = await apiRequest(
          "GET",
          `/api/admin/crm/criteria/${selectedCriteriaId}/conversions`
        );
        return response.json();
      },
      staleTime: 30_000,
    });

  const {
    data: dropoutData,
    isLoading: isDropoutLoading,
    error: dropoutError,
  } = useQuery<DropoutStatsResponse>({
    queryKey: ["bulk-import-dropouts", selectedCriteriaId],
    enabled: Boolean(selectedCriteriaId),
    queryFn: async () => {
      if (!selectedCriteriaId) {
        throw new Error("No criteria selected");
      }
      const response = await apiRequest(
        "GET",
        `/api/admin/crm/criteria/${selectedCriteriaId}/dropouts`
      );
      return response.json();
    },
    staleTime: 30_000,
  });

  const {
    data: contactsData,
    isLoading: isContactsLoading,
    error: contactsError,
  } = useQuery<BulkContactsResponse>({
    queryKey: ["bulk-import-contacts", selectedCriteriaId, contactsOffset],
    enabled: Boolean(selectedCriteriaId),
    queryFn: async () => {
      if (!selectedCriteriaId) {
        throw new Error("No criteria selected");
      }
      const params = new URLSearchParams({
        limit: "100",
        offset: String(contactsOffset),
      });
      const response = await apiRequest(
        "GET",
        `/api/admin/crm/criteria/${selectedCriteriaId}/contacts?${params.toString()}`
      );
      return response.json();
    },
    staleTime: 30_000,
  });

  const selectedCriteria = bulkCriteria.find((item) => item.id === selectedCriteriaId);

  const conversionRateDisplay =
    conversionData?.conversionRate !== undefined
      ? conversionData.conversionRate.toFixed(3)
      : "0.000";

  const dropoutRateDisplay =
    conversionData && dropoutData && conversionData.totalTracked > 0
      ? ((dropoutData.totalDropouts / conversionData.totalTracked) * 100).toFixed(3)
      : "0.000";

  return (
    <div className="space-y-6">
      <div className="grid gap-6 lg:grid-cols-[minmax(0,280px)_1fr]">
        <Card className="h-full">
          <CardHeader>
            <div className="flex items-center justify-between gap-2">
              <CardTitle className="text-sm font-semibold flex items-center gap-1">
                <Users className="h-4 w-4" />
                Bulk import criteria
              </CardTitle>
            </div>
            <CardDescription className="text-xs">
              Choose a criteria to inspect how many contacts converted.
            </CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            <ScrollArea className="max-h-[420px]">
              <div className="space-y-2 p-3">
                {isCriteriaLoading && (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Loading criteria...
                  </div>
                )}
                {criteriaError && (
                  <Alert variant="destructive">
                    <AlertTitle>Unable to load criteria</AlertTitle>
                    <AlertDescription>
                      {criteriaError.message || "Try refreshing the page."}
                    </AlertDescription>
                  </Alert>
                )}
                {!isCriteriaLoading && !bulkCriteria.length && (
                  <p className="text-xs text-muted-foreground">
                    No bulk import criteria found. Create one from the CRM tab first.
                  </p>
                )}
                {bulkCriteria.map((criteria) => {
                  const isSelected = selectedCriteriaId === criteria.id;
                  const totalTrackedForSelected =
                    isSelected && conversionData?.totalTracked !== undefined
                      ? conversionData.totalTracked
                      : undefined;
                  const displayCount =
                    totalTrackedForSelected ??
                    criteria.matchingUserCount ??
                    0;

                  return (
                    <Button
                      key={criteria.id}
                      variant={isSelected ? "secondary" : "ghost"}
                      size="sm"
                      className="w-full justify-start"
                      onClick={() => setSelectedCriteriaId(criteria.id)}
                    >
                      <div className="flex-1 text-left text-sm font-medium">
                        {criteria.name}
                      </div>
                      <Badge
                        variant={isSelected ? "secondary" : "outline"}
                        className={
                          isSelected
                            ? "bg-white/10 text-white border-white/20"
                            : undefined
                        }
                      >
                        {displayCount} saved
                      </Badge>
                    </Button>
                  );
                })}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>

        <div className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-sm font-semibold">
                {selectedCriteria?.name || "Select a criteria"}
              </CardTitle>
              <CardDescription className="text-xs">
                {selectedCriteria?.description ||
                  "Conversion stats are derived from tracked bulk import contacts."}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 sm:grid-cols-3">
                <div className="space-y-1 border rounded-md p-3">
                  <p className="text-xs text-muted-foreground">Pending uploads</p>
                  <p className="text-2xl font-semibold">{conversionData?.pendingCount ?? 0}</p>
                </div>
                <div className="space-y-1 border rounded-md p-3">
                  <p className="text-xs text-muted-foreground">Converted users</p>
                  <p className="text-2xl font-semibold">{conversionData?.convertedCount ?? 0}</p>
                </div>
                <div className="space-y-1 border rounded-md p-3">
                  <p className="text-xs text-muted-foreground">Dropouts</p>
                  <p className="text-2xl font-semibold">
                    {dropoutData?.totalDropouts ?? 0}
                  </p>
                </div>
              </div>

              <div className="mt-6 border rounded-md p-4">
                <Tabs defaultValue="overview" className="space-y-4">
                  <TabsList>
                    <TabsTrigger value="overview">Overview</TabsTrigger>
                    <TabsTrigger value="conversions">Conversions</TabsTrigger>
                    <TabsTrigger value="dropouts">Dropouts</TabsTrigger>
                  </TabsList>
                  <TabsContent value="overview">
                    <p className="text-sm text-muted-foreground mb-3">
                      Total tracked: {conversionData?.totalTracked ?? 0} contacts.
                    </p>
                    <p className="text-sm">
                      Conversion tracking removes entries from the contacts table when a user registers
                      with the same email or phone.
                    </p>
                    <div className="mt-4 space-y-2">
                      {contactsError && (
                        <Alert variant="destructive">
                          <AlertTitle>Unable to load contacts</AlertTitle>
                          <AlertDescription>
                            {contactsError instanceof Error
                              ? contactsError.message
                              : "Try again shortly."}
                          </AlertDescription>
                        </Alert>
                      )}
                      {isContactsLoading && (
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <Loader2 className="h-4 w-4 animate-spin" />
                          Loading pending contacts...
                        </div>
                      )}
                      {contactsData && contactsData.contacts.length > 0 && (
                        <>
                          <p className="text-xs text-muted-foreground">
                            Showing {contactsData.contacts.length} of{" "}
                            {contactsData.totalPending} pending contacts (page{" "}
                            {contactsOffset / (contactsData.limit || 1) + 1} of{" "}
                            {Math.max(
                              1,
                              Math.ceil(
                                contactsData.totalPending /
                                  (contactsData.limit || 1)
                              )
                            )}
                            ).
                          </p>
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead>First name</TableHead>
                                <TableHead>Last name</TableHead>
                                <TableHead>Email</TableHead>
                                <TableHead>Phone</TableHead>
                                <TableHead>Status</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {contactsData.contacts.map((contact) => (
                                <TableRow key={contact.id}>
                                  <TableCell className="text-xs">
                                    {contact.firstName}
                                  </TableCell>
                                  <TableCell className="text-xs">
                                    {contact.lastName}
                                  </TableCell>
                                  <TableCell className="text-xs">
                                    {contact.email}
                                  </TableCell>
                                  <TableCell className="text-xs">
                                    {contact.phone || "—"}
                                  </TableCell>
                                  <TableCell className="text-xs">Pending</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                          <div className="flex items-center justify-between mt-3">
                            <Button
                              variant="outline"
                              size="sm"
                              disabled={contactsOffset === 0}
                              onClick={() =>
                                setContactsOffset(
                                  Math.max(
                                    0,
                                    contactsOffset -
                                      (contactsData?.limit || 100)
                                  )
                                )
                              }
                            >
                              Previous
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              disabled={
                                contactsData.totalPending <=
                                contactsOffset + contactsData.contacts.length
                              }
                              onClick={() =>
                                setContactsOffset(
                                  contactsOffset +
                                    (contactsData?.limit || 100)
                                )
                              }
                            >
                              Next
                            </Button>
                          </div>
                        </>
                      )}
                      {contactsData &&
                        contactsData.contacts.length === 0 &&
                        !isContactsLoading &&
                        !contactsError && (
                          <p className="text-xs text-muted-foreground">
                            No pending contacts for this criteria.
                          </p>
                        )}
                    </div>
                  </TabsContent>
                  <TabsContent value="conversions">
                    <div className="mb-3 grid gap-3 sm:grid-cols-2 md:grid-cols-4">
                      <Card>
                        <CardContent className="p-3">
                          <p className="text-xs text-muted-foreground">
                            Conversion rate
                          </p>
                          <p className="text-lg font-semibold">
                            {conversionRateDisplay}%
                          </p>
                        </CardContent>
                      </Card>
                    </div>
                    {isConversionLoading && (
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Loading conversions...
                      </div>
                    )}
                    {conversionError && (
                      <Alert variant="destructive">
                        <AlertTitle>Unable to load conversions</AlertTitle>
                        <AlertDescription>
                          {conversionError.message || "Try again shortly."}
                        </AlertDescription>
                      </Alert>
                    )}
                    {!conversionError && !conversionData && !isConversionLoading && (
                      <p className="text-xs text-muted-foreground">
                        Select a criteria to view its conversions.
                      </p>
                    )}
                    {conversionData && conversionData.conversions.length > 0 && (
                      <ScrollArea className="max-h-60">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Converted at</TableHead>
                              <TableHead>Email</TableHead>
                              <TableHead>Phone</TableHead>
                              <TableHead>User</TableHead>
                              <TableHead>Source</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {conversionData.conversions.map((conversion) => (
                              <TableRow key={conversion.id}>
                                <TableCell className="text-xs">
                                  {format(
                                    new Date(conversion.createdAt),
                                    "d MMM yyyy, HH:mm"
                                  )}
                                </TableCell>
                                <TableCell className="text-xs">
                                  {conversion.email}
                                </TableCell>
                                <TableCell className="text-xs">
                                  {conversion.phone || "—"}
                                </TableCell>
                                <TableCell className="text-xs">
                                  {conversion.userId
                                    ? `User #${conversion.userId}`
                                    : "—"}
                                </TableCell>
                                <TableCell className="text-xs">
                                  {conversion.source || "—"}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </ScrollArea>
                    )}
                    {conversionData && conversionData.conversions.length === 0 && (
                      <p className="text-xs text-muted-foreground">
                        No conversions yet for this criteria.
                      </p>
                    )}
                  </TabsContent>
                  <TabsContent value="dropouts">
                    <div className="mb-3 grid gap-3 sm:grid-cols-2 md:grid-cols-4">
                      <Card>
                        <CardContent className="p-3">
                          <p className="text-xs text-muted-foreground">
                            Dropout rate
                          </p>
                          <p className="text-lg font-semibold">
                            {dropoutRateDisplay}%
                          </p>
                        </CardContent>
                      </Card>
                    </div>
                    {isDropoutLoading && (
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Loading dropouts...
                      </div>
                    )}
                    {dropoutError && (
                      <Alert variant="destructive">
                        <AlertTitle>Unable to load dropouts</AlertTitle>
                        <AlertDescription>
                          {dropoutError instanceof Error
                            ? dropoutError.message
                            : "Try again shortly."}
                        </AlertDescription>
                      </Alert>
                    )}
                    {!dropoutError && !dropoutData && !isDropoutLoading && (
                      <p className="text-xs text-muted-foreground">
                        Select a criteria to view its dropouts.
                      </p>
                    )}
                    {dropoutData && dropoutData.dropouts.length > 0 && (
                      <ScrollArea className="max-h-60">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Unsubscribed at</TableHead>
                              <TableHead>Email</TableHead>
                              <TableHead>Phone</TableHead>
                              <TableHead>User</TableHead>
                              <TableHead>Reason</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {dropoutData.dropouts.map((dropout) => (
                              <TableRow key={dropout.id}>
                                <TableCell className="text-xs">
                                  {format(
                                    new Date(dropout.unsubscribedAt),
                                    "d MMM yyyy, HH:mm"
                                  )}
                                </TableCell>
                                <TableCell className="text-xs">{dropout.email}</TableCell>
                                <TableCell className="text-xs">
                                  {dropout.phone || "—"}
                                </TableCell>
                                <TableCell className="text-xs">
                                  {dropout.userId ? `User #${dropout.userId}` : "—"}
                                </TableCell>
                                <TableCell className="text-xs">
                                  {dropout.reason || "—"}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </ScrollArea>
                    )}
                    {dropoutData && dropoutData.dropouts.length === 0 && (
                      <p className="text-xs text-muted-foreground">
                        No dropouts yet for this criteria.
                      </p>
                    )}
                  </TabsContent>
                </Tabs>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
